import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Grid, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Alert,
  Snackbar,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Cancel as CancelIcon, 
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon 
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import es from 'date-fns/locale/es';
import formularioService from '../../services/formularioService';
import historiaService from '../../services/historiaService';
import { TablaDinamica, TablaEvaluacion } from './TablaFormularioComponents';
import TablaPlanTerapeutico from './campos/TablaPlanTerapeutico';
import SeleccionObjetivoPadre from './campos/SeleccionObjetivoPadre';
import TablaEvaluacionMensual from './campos/TablaEvaluacionMensual';


 // Agregar estas funciones ANTES de validarFormulario() en FormularioEditor.jsx

// Valores válidos que no requieren validación de caracteres mínimos
const RESPUESTAS_VALIDAS_CORTAS = [
  // Respuestas clínicas estándar
  'no aplica', 'n/a', 'na', '-', '--', 'sin datos', 'ninguno', 'ninguna',
  'no presenta', 'no refiere', 'niega', 'negativo', 'normal', 'sin hallazgos',
  'no significativo', 'no relevante', 'sin antecedentes', 'no corresponde',
  
  // Caracteres mínimos para indicar "campo completado pero sin información"
  '.', '..', '...', '*', '**', '_', '__', 'x', 'xx', 'ok', 'si', 'sí', 'no'
];

// Función para verificar si una respuesta es válida aunque sea corta
const esRespuestaValidaCorta = (texto) => {
  if (!texto || typeof texto !== 'string') return false;
  const textoLimpio = texto.toLowerCase().trim();
  return RESPUESTAS_VALIDAS_CORTAS.includes(textoLimpio);
};

// Función helper para validar caracteres mínimos en campos de tabla
const validarCaracteresMinimosTabla = (campo, value) => {
  // Solo validar que no esté vacío, no validar longitud mínima
  const minChars = campo.min_chars || campo.configuracion?.min_chars || 0;
  if (minChars === 0) return { valido: true }; // Sin restricción
  
  switch (campo.tipo) {
    case 'tabla_dinamica':
      return validarTablaDinamica(value, campo.configuracion, minChars);
      
    case 'tabla_evaluacion':
      return validarTablaEvaluacion(value, campo.configuracion, minChars);
      
    case 'tabla_plan_terapeutico':
    case 'tabla_plan_neuro':
      return validarTablaPlanTerapeutico(value, campo.configuracion, minChars);
      
    case 'tabla_evaluacion_mensual':
      return validarTablaEvaluacionMensual(value, campo.configuracion, minChars);
      
    default:
      return { valido: true };
  }
};

const validarTablaDinamica = (value, configuracion, minChars) => {
  if (!Array.isArray(value) || value.length === 0) {
    return { valido: false, mensaje: 'Debe agregar al menos una fila' };
  }
  
  for (let i = 0; i < value.length; i++) {
    const fila = value[i];
    
    // Verificar columnas que requieren contenido (solo no vacías)
    const columnasTexto = configuracion.columnas.filter(col => 
      col.tipo === 'texto_largo' && col.min_chars > 0
    );
    
    for (const columna of columnasTexto) {
      const texto = fila[columna.id] || '';
      
      // Solo verificar que no esté vacío, cualquier texto es válido
      if (texto.trim().length === 0) {
        return {
          valido: false,
          mensaje: `Fila ${i + 1}, ${columna.etiqueta}: este campo es requerido`
        };
      }
    }
  }
  
  return { valido: true };
};

const validarTablaEvaluacion = (value, configuracion, minChars) => {
  if (!value || typeof value !== 'object') {
    return { valido: false, mensaje: 'Debe completar al menos una evaluación' };
  }
  
  // Verificar columnas de texto que requieren contenido (solo no vacías)
  const columnasTexto = configuracion.columnas.filter(col => 
    col.tipo === 'texto_largo' && col.min_chars > 0
  );
  
  if (columnasTexto.length === 0) return { valido: true };
  
  for (const grupo of configuracion.areas) {
    for (const item of grupo.items) {
      const evaluacion = value[item.id];
      if (evaluacion) {
        for (const columna of columnasTexto) {
          const texto = evaluacion[columna.id] || '';
          
          // Solo verificar que no esté vacío, cualquier texto es válido
          if (texto.trim().length === 0) {
            return {
              valido: false,
              mensaje: `${item.nombre} - ${columna.etiqueta}: este campo es requerido`
            };
          }
        }
      }
    }
  }
  
  return { valido: true };
};

const validarTablaPlanTerapeutico = (value, configuracion, minChars) => {
  if (!Array.isArray(value) || value.length === 0) {
    return { valido: false, mensaje: 'Debe agregar al menos un área de intervención' };
  }
  
  for (let i = 0; i < value.length; i++) {
    const area = value[i];
    
    // Validar área (solo que no esté vacío)
    const campoArea = configuracion.columnas?.find(c => c.id === 'area');
    if (campoArea?.min_chars > 0) {
      const textoArea = area.area || '';
      if (textoArea.trim().length === 0) {
        return {
          valido: false,
          mensaje: `Área ${area.numero}: el nombre del área es requerido`
        };
      }
    }
    
    // Validar objetivos generales (solo que no esté vacío)
    const campoObjGenerales = configuracion.columnas?.find(c => c.id === 'objetivos_generales');
    if (campoObjGenerales?.min_chars > 0) {
      const textoObjGenerales = area.objetivos_generales || '';
      if (textoObjGenerales.trim().length === 0) {
        return {
          valido: false,
          mensaje: `Área ${area.numero}: los objetivos generales son requeridos`
        };
      }
    }
    
    // Validar objetivos específicos (solo que no esté vacío)
    const campoObjEspecificos = configuracion.columnas?.find(c => c.id === 'objetivos_especificos');
    if (campoObjEspecificos?.min_chars > 0 && area.objetivos_especificos) {
      for (let j = 0; j < area.objetivos_especificos.length; j++) {
        const objetivo = area.objetivos_especificos[j];
        
        // Validar texto del objetivo (solo que no esté vacío)
        if (objetivo.texto && objetivo.texto.trim().length === 0) {
          return {
            valido: false,
            mensaje: `Área ${area.numero}, Objetivo ${j + 1}: el texto del objetivo es requerido`
          };
        }
        
        // Validar estrategias (solo que no esté vacío si se configuró)
        if (objetivo.estrategias_actividades && configuracion.min_chars_estrategias > 0) {
          if (objetivo.estrategias_actividades.trim().length === 0) {
            return {
              valido: false,
              mensaje: `Área ${area.numero}, Objetivo ${j + 1}: las estrategias son requeridas`
            };
          }
        }
        
        // Validar metas (solo que no esté vacío si se configuró)
        if (objetivo.metas_medicion && configuracion.min_chars_metas > 0) {
          if (objetivo.metas_medicion.trim().length === 0) {
            return {
              valido: false,
              mensaje: `Área ${area.numero}, Objetivo ${j + 1}: las metas son requeridas`
            };
          }
        }
      }
    }
  }
  
  return { valido: true };
};

const validarTablaEvaluacionMensual = (value, configuracion, minChars) => {
  if (!value || typeof value !== 'object') {
    return { valido: false, mensaje: 'Debe completar la evaluación mensual' };
  }
  
  const evaluaciones = value.evaluaciones || {};
  const objetivosSeleccionados = value.objetivos_seleccionados || [];
  
  if (objetivosSeleccionados.length === 0) {
    return { valido: false, mensaje: 'Debe seleccionar al menos un objetivo para evaluar' };
  }
  
  // Validar notas detalladas solo que no estén vacías si se configuró
  if (configuracion.min_chars_notas > 0) {
    for (const objId of objetivosSeleccionados) {
      const evaluacion = evaluaciones[objId];
      if (evaluacion && evaluacion.notas_detalladas !== undefined) {
        const notas = evaluacion.notas_detalladas;
        if (notas.trim().length === 0) {
          return {
            valido: false,
            mensaje: `Objetivo ${objId}: las notas son requeridas`
          };
        }
      }
    }
  }
  
  return { valido: true };
};

const FormularioEditor = ({ modo = 'crear' }) => {
  const { formularioId, historiaId, respuestaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const parentRespuestaId = query.get('parent');
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formulario, setFormulario] = useState(null);
  const [historia, setHistoria] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [respuestasHijosPrevias, setRespuestasHijosPrevias] = useState([]);
  const [respuestaOriginal, setRespuestaOriginal] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  const [esEditable, setEsEditable] = useState(true);
  const [secciones, setSecciones] = useState([]);
  const [campos, setCampos] = useState([]);
  const [camposAgrupados, setCamposAgrupados] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos();
  }, [formularioId, historiaId, respuestaId, modo]);

  // Cargar respuestas previas si es formulario hijo
  useEffect(() => {
    if (parentRespuestaId && formulario?.tipo_dependencia?.includes('hijo')) {
      cargarRespuestasPrevias();
    }
  }, [parentRespuestaId, formulario]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar formulario - AJUSTADO AL MÉTODO DE TU SERVICIO
      const formularioData = await formularioService.getFormulario(formularioId);
      setFormulario(formularioData);
      
      // Procesar estructura del formulario
      procesarEstructuraFormulario(formularioData);
      
      // Cargar historia clínica
      const historiaData = await historiaService.getHistoria(historiaId);
      setHistoria(historiaData);
      
      // Si es modo edición, cargar respuesta existente
      if (modo === 'editar' && respuestaId) {
        // AJUSTADO AL MÉTODO DE TU SERVICIO
        const respuestaData = await formularioService.getRespuesta(respuestaId);
        setRespuestaOriginal(respuestaData);
        setRespuestas(respuestaData?.respuestas || {});
        
        // Verificar si es editable
        const estadoResp = await formularioService.getEstadoFormulario(respuestaId);
        setEsEditable(estadoResp.data?.esEditable || false);
      }
      
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setAlert({
        open: true,
        message: 'Error al cargar los datos del formulario',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarRespuestasPrevias = async () => {
    try {
      const respuestas = await formularioService.getRespuestasHijo(parentRespuestaId);
      setRespuestasHijosPrevias(respuestas.data || []);
    } catch (error) {
      console.error('Error al cargar respuestas previas:', error);
    }
  };

  const procesarEstructuraFormulario = (formularioData) => {
    if (!formularioData || !formularioData.estructura) return;
    
    try {
      let estructura = formularioData.estructura;
      
      if (typeof estructura === 'string') {
        estructura = JSON.parse(estructura);
      }
      
      const seccionesArray = estructura.secciones || [];
      const camposArray = estructura.campos || [];
      
      setSecciones(seccionesArray);
      setCampos(camposArray);
      
      // Agrupar campos por sección
      const agrupados = {};
      
      if (seccionesArray.length > 0) {
        seccionesArray.forEach(seccion => {
          agrupados[seccion.id] = camposArray.filter(
            campo => campo.seccion_id === seccion.id
          );
        });
        
        // Campos sin sección
        const camposSinSeccion = camposArray.filter(
          campo => !campo.seccion_id || !seccionesArray.find(s => s.id === campo.seccion_id)
        );
        
        if (camposSinSeccion.length > 0) {
          agrupados['sin_seccion'] = camposSinSeccion;
        }
      } else {
        agrupados['general'] = camposArray;
      }
      
      setCamposAgrupados(agrupados);
      
    } catch (error) {
      console.error('Error al procesar estructura del formulario:', error);
      setAlert({
        open: true,
        message: 'Error al procesar la estructura del formulario',
        severity: 'error'
      });
    }
  };

  
// MODIFICAR la función validarFormulario() existente:
const validarFormulario = () => {
 
  
  const camposRequeridos = campos.filter(campo => campo.requerido);
   
  for (const campo of camposRequeridos) {
     const valor = respuestas[campo.id];
     
    if (!valor || (Array.isArray(valor) && valor.length === 0)) {
       setAlert({
        open: true,
        message: `El campo "${campo.etiqueta}" es requerido`,
        severity: 'error'
      });
      return false;
    }
    
    // NUEVA VALIDACIÓN: Verificar caracteres mínimos para tablas
    if (['tabla_dinamica', 'tabla_evaluacion', 'tabla_plan_terapeutico', 'tabla_plan_neuro', 'tabla_evaluacion_mensual'].includes(campo.tipo)) {
  
      const validacionTabla = validarCaracteresMinimosTabla(campo, valor);
       
      if (!validacionTabla.valido) {
         setAlert({
          open: true,
          message: `${campo.etiqueta}: ${validacionTabla.mensaje}`,
          severity: 'error'
        });
        return false;
      }
    }
  }
  
   return true;
};

  const handleChange = (fieldId, value) => {
    setRespuestas(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };


  const handleSubmit = async () => {
    if (!validarFormulario()) return;
    
    try {
      setSaving(true);
      
      const datosBase = {
        formulario_id: formularioId,
        historia_especialidad_id: historia?.id, 
        paciente_id: historia?.paciente_id,
        especialista_id: historia?.especialista_id || 1,
        fecha_completado: new Date(),
        respuestas: respuestas,
        parent_respuesta_id: parentRespuestaId || null
      };
      
      // Si es formulario hijo cualitativo, agregar objetivos seleccionados
      if (formulario?.tipo_dependencia === 'hijo_cualitativo' && respuestas.objetivo_seleccionado) {
        datosBase.objetivos_seleccionados = [respuestas.objetivo_seleccionado];
      }
      
      let response;
      
      if (modo === 'editar' && respuestaId) {
        response = await formularioService.actualizarRespuesta(respuestaId, datosBase);
      } else {
        // Usar el endpoint correcto según el tipo de formulario
        if (formulario?.tipo_dependencia?.includes('hijo')) {
          response = await formularioService.guardarRespuestaHijo(datosBase);
        } else {
          response = await formularioService.guardarRespuesta(datosBase);
        }
      }
      
      if (response.success || response.data || response) {
        setAlert({
          open: true,
          message: 'Formulario guardado correctamente',
          severity: 'success'
        });
        
        setTimeout(() => {
          navigate(`/historias/${historiaId}`);
        }, 1500);
      }
      
    } catch (error) {
      console.error('Error al guardar formulario:', error);
      setAlert({
        open: true,
        message: error.response?.data?.message || 'Error al guardar el formulario',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('¿Está seguro de cancelar? Los cambios no guardados se perderán.')) {
      navigate(`/historias/${historiaId}`);
    }
  };

  const renderField = (campo) => {
    const value = respuestas[campo.id];
    
    switch (campo.tipo) {
      case 'texto_corto':
        return (
          <TextField
            fullWidth
            label={campo.etiqueta}
            value={value || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            required={campo.requerido}
            disabled={!esEditable}
            placeholder={campo.placeholder}
            helperText={campo.ayuda}
          />
        );

      case 'texto_largo':
        return (
          <TextField
            fullWidth
            multiline
            rows={campo.filas || 4}
            label={campo.etiqueta}
            value={value || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            required={campo.requerido}
            disabled={!esEditable}
            placeholder={campo.placeholder}
            helperText={campo.ayuda}
          />
        );

      case 'numero':
        return (
          <TextField
            fullWidth
            type="number"
            label={campo.etiqueta}
            value={value || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            required={campo.requerido}
            disabled={!esEditable}
            inputProps={{ min: campo.min, max: campo.max }}
            helperText={campo.ayuda}
          />
        );

      case 'fecha':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              label={campo.etiqueta}
              value={value ? new Date(value) : null}
              onChange={(newValue) => handleChange(campo.id, newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  fullWidth 
                  required={campo.requerido}
                  helperText={campo.ayuda}
                />
              )}
              disabled={!esEditable}
            />
          </LocalizationProvider>
        );

      case 'seleccion':
        return (
          <FormControl fullWidth required={campo.requerido} disabled={!esEditable}>
            <InputLabel>{campo.etiqueta}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleChange(campo.id, e.target.value)}
              label={campo.etiqueta}
            >
              <MenuItem value="">
                <em>Seleccione una opción</em>
              </MenuItem>
              {campo.opciones?.map((opcion) => (
                <MenuItem key={opcion.valor} value={opcion.valor}>
                  {opcion.etiqueta}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value || false}
                onChange={(e) => handleChange(campo.id, e.target.checked)}
                disabled={!esEditable}
              />
            }
            label={campo.etiqueta}
          />
        );

      case 'tabla_dinamica':
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {campo.etiqueta}
              {campo.requerido && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TablaDinamica
              campo={campo}
              value={value || []}
              onChange={(newValue) => handleChange(campo.id, newValue)}
            />
          </Box>
        );

      case 'tabla_evaluacion':
        return (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {campo.etiqueta}
              {campo.requerido && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TablaEvaluacion
              campo={campo}
              value={value || {}}
              onChange={(newValue) => handleChange(campo.id, newValue)}
            />
          </Box>
        );

      case 'tabla_plan_terapeutico':
      case 'tabla_plan_neuro':
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {campo.etiqueta}
              {campo.requerido && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TablaPlanTerapeutico
              campo={campo}
              value={respuestas[campo.id] || []}
              onChange={(value) => handleChange(campo.id, value)}
              disabled={!esEditable}
            />
          </Box>
        );
        
      case 'seleccion_objetivo_padre':
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {campo.etiqueta}
              {campo.requerido && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <SeleccionObjetivoPadre
              campo={campo}
              value={respuestas[campo.id]}
              onChange={(value) => handleChange(campo.id, value)}
              parentRespuestaId={parentRespuestaId}
              disabled={!esEditable}
              respuestasAnteriores={respuestasHijosPrevias}
            />
          </Box>
        );
        
      case 'tabla_evaluacion_mensual':
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {campo.etiqueta}
              {campo.requerido && <span style={{ color: 'red' }}> *</span>}
            </Typography>
            <TablaEvaluacionMensual
              campo={campo}
              value={respuestas[campo.id] || {}}
              onChange={(value) => handleChange(campo.id, value)}
              parentRespuestaId={parentRespuestaId}
              disabled={!esEditable}
            />
          </Box>
        );
        
      case 'lista_objetivos':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            label={campo.etiqueta}
            value={value || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            disabled={!esEditable}
            placeholder={campo.placeholder || "Lista de objetivos (uno por línea)"}
            helperText="Ingrese un objetivo por línea"
            required={campo.requerido}
          />
        );

      case 'mes':
        return (
          <TextField
            fullWidth
            type="month"
            label={campo.etiqueta}
            value={value || ''}
            onChange={(e) => handleChange(campo.id, e.target.value)}
            required={campo.requerido}
            disabled={!esEditable}
            InputLabelProps={{ shrink: true }}
          />
        );

      default:
        return (
          <Typography color="error" variant="body2">
            Tipo de campo no soportado: {campo.tipo}
          </Typography>
        );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!formulario) {
    return (
      <Box p={3}>
        <Alert severity="error">
          No se pudo cargar el formulario
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/historias')}
          sx={{ mt: 2 }}
        >
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Encabezado */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate(`/historias/${historiaId}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 2 }}>
            {modo === 'editar' ? 'Editar' : 'Completar'} {formulario.nombre}
          </Typography>
        </Box>
        
        {!esEditable && (
          <Alert severity="warning" sx={{ ml: 2 }}>
            Este formulario no es editable
          </Alert>
        )}
      </Box>

      {/* Información del paciente */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Paciente
              </Typography>
              <Typography variant="body1">
                {historia?.paciente?.nombres} {historia?.paciente?.apellidos}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                DNI
              </Typography>
              <Typography variant="body1">
                {historia?.paciente?.dni || 'No registrado'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Historia Clínica
              </Typography>
              <Typography variant="body1">
                {historia?.paciente?.numero_historia || 'No registrado'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Formulario */}
      <Paper sx={{ p: 3 }}>
        {formulario.descripcion && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {formulario.descripcion}
          </Alert>
        )}

        {/* Renderizar campos por sección */}
        {secciones.length > 0 ? (
          secciones.map((seccion, index) => (
            <Box key={seccion.id} mb={4}>
              <Typography variant="h6" gutterBottom>
                {seccion.titulo}
              </Typography>
              {seccion.descripcion && (
                <Typography variant="body2" color="textSecondary" paragraph>
                  {seccion.descripcion}
                </Typography>
              )}
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                {camposAgrupados[seccion.id]?.map((campo) => (
                  <Grid item xs={12} md={campo.tipo === 'checkbox' ? 6 : 12} key={campo.id}>
                    {renderField(campo)}
                  </Grid>
                ))}
              </Grid>
              
              {index < secciones.length - 1 && <Box mt={3} />}
            </Box>
          ))
        ) : (
          // Si no hay secciones, renderizar todos los campos
          <Grid container spacing={2}>
            {campos.map((campo) => (
              <Grid item xs={12} md={campo.tipo === 'checkbox' ? 6 : 12} key={campo.id}>
                {renderField(campo)}
              </Grid>
            ))}
          </Grid>
        )}

        {/* Botones de acción */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving || !esEditable}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </Paper>

      {/* Notificaciones */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormularioEditor;